import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react'
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Popover,
  MenuItem,
  ListItemText,
  TextField,
  InputAdornment,
  IconButton,
  Drawer,
  List,
  ListItem,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import AccountCircle from '@mui/icons-material/AccountCircle';
import MenuIcon from '@mui/icons-material/Menu';
import { useOrgUser } from '../context/OrgUserProvider';

const Navbar = () => {
  const { getAccessTokenSilently, logout, isAuthenticated, loginWithRedirect } = useAuth0()
  const [collectionsAnchorEl, setCollectionsAnchorEl] = useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSubmenu, setMobileSubmenu] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const collectionsOpen = Boolean(collectionsAnchorEl);
  const profileOpen = Boolean(profileAnchorEl);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { orgUser } = useOrgUser()

  const handleNavigation = (path) => {
    navigate(path);
    setCollectionsAnchorEl(null);
    setMobileOpen(false);
    setMobileSubmenu(null);
  };

  const handleLogin = () => {
    loginWithRedirect();
  };

  const handleSignUp = () => {
    window.location.href = "https://signup.qallix.com/";
  };

  const handleLogout = async () => {
    sessionStorage.clear()
    await logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    })
  }

  const handleCollectionsHover = (event) => {
    if (!isMobile) {
      setCollectionsAnchorEl(event.currentTarget);
    }
  };

  const handleCollectionsClose = () => {
    setCollectionsAnchorEl(null);
  };

  const handleProfileClick = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
    setMobileSubmenu(null);
  };

  const handleMobileSubmenu = (itemName) => {
    setMobileSubmenu(mobileSubmenu === itemName ? null : itemName);
  };

  const navItems = [
    { name: 'Home', path: '/' },
    {
      name: 'Collections',
      subItems: [
        { name: 'Basic', path: '/collection-basic' },
        { name: 'Intermediate', path: '/collection-intermediate' },
        { name: 'Advanced', path: '/collection-advanced' }
      ]
    },
    { name: 'Courses', path: '/courses' }
  ];

  const drawer = (
    <Box sx={{ width: 250, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box
          component="img"
          src={`https://qallix-public.s3.ap-southeast-1.amazonaws.com/QallixAcademy/theme/assets/favicon.ico`}
          alt="Logo"
          sx={{ height: '40px', objectFit: 'contain' }}
          onClick={() => handleNavigation('/')}
        />
        <IconButton onClick={handleDrawerToggle}>
        </IconButton>
      </Box>
      <Divider />
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <List>
          {navItems.map((item) => (
            item.subItems ? (
              <Box key={item.name}>
                <ListItem
                  button
                  onClick={() => handleMobileSubmenu(item.name)}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                  }}
                >
                  <ListItemText
                    primary={item.name}
                    primaryTypographyProps={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 'bold' }}
                  />
                  {mobileSubmenu === item.name ? <ExpandLess /> : <ExpandMore />}
                </ListItem>
                {mobileSubmenu === item.name && (
                  <List sx={{ pl: 2, backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                    {item.subItems.map((subItem) => (
                      <ListItem
                        key={subItem.name}
                        button
                        onClick={() => handleNavigation(subItem.path)}
                        sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                      >
                        <ListItemText
                          primary={subItem.name}
                          primaryTypographyProps={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 'bold' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            ) : (
              <ListItem
                key={item.name}
                button
                onClick={() => handleNavigation(item.path)}
                sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
              >
                <ListItemText
                  primary={item.name}
                  primaryTypographyProps={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 'bold' }}
                />
              </ListItem>
            )
          ))}
        </List>
      </Box>
      <Divider />
      <Box sx={{ p: 2 }}>
      </Box>
      {isAuthenticated ? (
        <>
          <Box sx={{ p: 2 }}>
            <Button
              fullWidth
              startIcon={<AccountCircle />}
              onClick={() => handleNavigation('/profile')}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                fontFamily: "'Satoshi', sans-serif",
                fontWeight: 'bold'
              }}
            >
              My Profile
            </Button>
          </Box>
          <Box sx={{ p: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleLogout}
              sx={{
                textTransform: 'none',
                fontFamily: "'Satoshi', sans-serif",
                fontWeight: 'bold'
              }}
            >
              Logout
            </Button>
          </Box>
        </>
      ) : (
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleLogin}
            sx={{
              textTransform: 'none',
              fontFamily: "'Satoshi', sans-serif",
              fontWeight: 'bold'
            }}
          >
            Login
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleSignUp}
            sx={{
              textTransform: 'none',
              fontFamily: "'Satoshi', sans-serif",
              fontWeight: 'bold'
            }}
          >
            Sign Up
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: 'rgba(255, 255, 255)',
        color: '#000',
        boxShadow: 'none',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        position: '-webkit-sticky',
        top: 0,
        zIndex: 1200,
        height: '80px',
        display: 'flex',
        justifyContent: 'center',
        height: { xs: '50px', sm: '80px' }
      }}
    >
      <Toolbar sx={{
        display: 'flex',
        justifyContent: 'space-between',
        margin: '0 auto',
        width: '100%',
        maxWidth: '1200px',
        padding: { xs: '10px' }
      }}>
        {/* Mobile Layout using CSS Grid */}
        {isMobile ? (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr auto',
            alignItems: 'center',
            width: '100%'
          }}>
            {/* Mobile menu button - left aligned */}
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ ml: 0.5 }}
            >
              <MenuIcon />
            </IconButton>

            {/* Logo - centered in the grid */}
            <Box
              component="img"
              src={`https://qallix-public.s3.ap-southeast-1.amazonaws.com/QallixAcademy/theme/assets/favicon.ico`}
              alt="Logo"
              sx={{
                height: '40px',
                objectFit: 'contain',
                cursor: 'pointer',
                justifySelf: 'center',
                maxWidth: '170px'
              }}
              onClick={() => handleNavigation('/')}
            />

            {/* Empty spacer to balance the grid */}
            <Box sx={{ width: '48px' }} />
          </Box>
        ) : (
          /* Desktop Layout */
          <>
            {/* Logo */}
            <Box
              component="img"
              src={`https://qallix-public.s3.ap-southeast-1.amazonaws.com/QallixAcademy/theme/assets/favicon.ico`}
              alt="Logo"
              sx={{
                height: '40px',
                objectFit: 'contain',
                cursor: 'pointer',
              }}
              onClick={() => handleNavigation('/')}
            />

            {/* Desktop navigation links */}
            <Box sx={{
              display: 'flex',
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)'
            }}>
              {navItems.map((item) => (
                item.subItems ? (
                  <Box
                    key={item.name}
                    onMouseEnter={handleCollectionsHover}
                    onMouseLeave={handleCollectionsClose}
                    sx={{ position: 'relative' }}
                  >
                    <Button
                      color="inherit"
                      endIcon={collectionsOpen ? <ExpandLess /> : <ExpandMore />}
                      sx={{
                        mx: 1,
                        fontFamily: "'Satoshi', sans-serif",
                        fontWeight: 'bold',
                        textTransform: 'none',
                        color: '#000',
                        '&:hover': {
                          color: 'rgba(0, 0, 0, 0.7)'
                        }
                      }}
                    >
                      {item.name}
                    </Button>
                    <Popover
                      open={collectionsOpen}
                      anchorEl={collectionsAnchorEl}
                      onClose={handleCollectionsClose}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                      sx={{
                        pointerEvents: 'none',
                        '& .MuiPopover-paper': {
                          pointerEvents: 'auto',
                          mt: 1,
                          minWidth: 180,
                          boxShadow: 3,
                          backgroundColor: 'white'
                        }
                      }}
                      disableRestoreFocus
                    >
                      {item.subItems.map((subItem) => (
                        <MenuItem
                          key={subItem.name}
                          onClick={() => handleNavigation(subItem.path)}
                          sx={{
                            '&:hover': {
                              color: 'rgba(0, 0, 0, 0.7)'
                            }
                          }}
                        >
                          <ListItemText
                            sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 'bold' }}
                            primary={subItem.name}
                          />
                        </MenuItem>
                      ))}
                    </Popover>
                  </Box>
                ) : (
                  <Button
                    key={item.name}
                    color="inherit"
                    onClick={() => handleNavigation(item.path)}
                    sx={{
                      mx: 1,
                      fontFamily: "'Satoshi', sans-serif",
                      fontWeight: 'bold',
                      textTransform: 'none',
                      color: '#000',
                      '&:hover': {
                        color: 'rgba(0, 0, 0, 0.7)'
                      }
                    }}
                  >
                    {item.name}
                  </Button>
                )
              ))}
            </Box>

            {/* Right side items - Only show on desktop */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {isAuthenticated ? (
                <>
                  {/* Profile icon for authenticated users */}
                  <Box sx={{ pr: 2 }}>
                    <IconButton onClick={handleProfileClick}>
                      <AccountCircle sx={{ fontSize: 32, color: '#000' }} />
                    </IconButton>
                  </Box>

                  <Popover
                    open={profileOpen}
                    anchorEl={profileAnchorEl}
                    onClose={handleProfileClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    <Box sx={{ p: 1, width: 200 }}>
                      <MenuItem onClick={() => handleNavigation('/profile')}>
                        <ListItemText primary="My Profile" />
                      </MenuItem>
                      {/* Only show Manage Courses for SuperAdmin and IT */}
                      {orgUser?.role === 'SuperAdmin' || 'IT' && (
                        <MenuItem onClick={() => handleNavigation('/manage-courses')}>
                          <ListItemText primary="Manage Courses" />
                        </MenuItem>
                      )}
                      <MenuItem onClick={handleLogout}>
                        <ListItemText primary="Logout" />
                      </MenuItem>
                    </Box>
                  </Popover>
                </>
              ) : (
                /* Login/Sign Up buttons for unauthenticated users */
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={handleLogin}
                    sx={{
                      textTransform: 'none',
                      fontFamily: "'Satoshi', sans-serif",
                      fontWeight: 'bold',
                      borderRadius: '20px',
                      height: '50px',
                      borderColor: '#332872',
                      color: '#332872'
                    }}
                  >
                    Login
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSignUp}
                    sx={{
                      textTransform: 'none',
                      fontFamily: "'Satoshi', sans-serif",
                      fontWeight: 'bold',
                      borderRadius: '20px',
                      backgroundColor: '#332872'
                    }}
                  >
                    Sign Up
                  </Button>
                </Box>
              )}
            </Box>
          </>
        )}
      </Toolbar>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
};

export default Navbar;